import { useInfiniteQuery, useQuery } from 'react-query'
import axios from 'axios'

import { handleCommonErrors } from 'utils/api'
import { NotFoundError } from 'utils/customError'
import { baseUrl } from 'configs/api'

import type { AxiosResponse } from 'axios'
import type { TContentSearchResult } from 'types/content-search'
import type { TDocument } from 'types/document-collection'

const API_DOCUMENT_COLLECTION_PATH =
  '/guru/teachers/v1alpha1/document-collection/categories'

const API_DOCUMENT_PATH =
  '/guru/teachers/v1alpha1/document-collection/documents'

export enum CATEGORY_TYPE_ENUM {
  CT_UNDEFINED = 'CT_UNDEFINED',
  CT_MAIN_CATEGORY = 'CT_MAIN_CATEGORY',
  CT_SUB_CATEGORY = 'CT_SUB_CATEGORY',
  CT_END_CATEGORY = 'CT_END_CATEGORY',
}

export type GetCategoryList = {
  id: string
  name: string
  description: string
  thumbnailUrl: string
  categoryType: CATEGORY_TYPE_ENUM
}

export type ResponseSuccessGetDocumentCollection = {
  data: TContentSearchResult[]
  meta: {
    limit: number
    offset: number
    total: number
    isNextPageError?: boolean
  }
}

export type GetDocumentCategoryDetail = {
  categoryType?: CATEGORY_TYPE_ENUM
  description?: string
  guideContent?: string
  guideThumbnailUrl?: string
  guideTitle?: string
  guideUrl?: string
  id?: string
  name?: string
  parentId?: string
  parentName?: string
  thumbnailUrl?: string
}

export type ResponseSuccessGetDocumentCategoryDetail = {
  data: GetDocumentCategoryDetail
}

export type ResponseSuccessGetDocumentCategoryList = {
  data: GetCategoryList[]
  meta: {
    limit: number
    offset: number
    total: number
    isNextPageError?: boolean
  }
}

export function handleError(status, path, error) {
  if (status >= 400 && status < 500) {
    throw new NotFoundError(path, error)
  } else {
    handleCommonErrors(status, path, error)
  }
}

export function useAPIDocumentCollectionCategoriesList(
  params: {
    offset?: number
    limit: number
    parentId?: string | string[]
  } = { offset: 0, limit: 10 }
) {
  return useInfiniteQuery<ResponseSuccessGetDocumentCategoryList, Error>(
    ['category-document-collection-list', params],
    async ({ pageParam }) => {
      try {
        const queryParams = {
          limit: pageParam?.limit || params?.limit,
          offset: pageParam?.offset || params?.offset,
          ...(Boolean(params?.parentId) ? { parentId: params?.parentId } : {}),
        }
        const response = (
          await axios.get<
            any,
            AxiosResponse<ResponseSuccessGetDocumentCategoryList>
          >(`${baseUrl}${API_DOCUMENT_COLLECTION_PATH}`, {
            params: queryParams,
          })
        ).data
        return response
      } catch (error) {
        if (pageParam?.offset >= (params?.limit || 10)) {
          const data = {
            data: [],
            meta: {
              ...(pageParam || params),
              total: pageParam?.total ?? 0,
              offset: pageParam?.offset ?? 0,
              isNextPageError: true,
            },
          }
          return data
        }

        handleError(
          error?.response?.status,
          `${API_DOCUMENT_COLLECTION_PATH}`,
          error.response || error
        )
      }
    },
    {
      useErrorBoundary: true,
      getNextPageParam({ meta }) {
        if (meta.total <= (meta?.offset ?? 0) + params.limit) return

        return {
          offset: meta.offset + params.limit,
          limit: params.limit,
          total: meta.total,
        }
      },
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000, // 1 hour
    }
  )
}

export function useAPIDocumentCollectionCategoriesDocumentList(
  id: string | string[],
  params: {
    offset?: number
    limit: number
  } = { offset: 0, limit: 10 }
) {
  return useInfiniteQuery<ResponseSuccessGetDocumentCollection, Error>(
    ['category-document-collection-list', id, params],
    async ({ pageParam }) => {
      try {
        const response = (
          await axios.get<
            any,
            AxiosResponse<ResponseSuccessGetDocumentCollection>
          >(`${baseUrl}${API_DOCUMENT_COLLECTION_PATH}/${id}/documents`, {
            params: pageParam || params,
          })
        ).data

        return response
      } catch (error) {
        if (pageParam?.offset >= (params?.limit || 10)) {
          const data = {
            data: [],
            meta: {
              ...(pageParam || params),
              total: pageParam?.total ?? 0,
              offset: pageParam?.offset ?? 0,
              isNextPageError: true,
            },
          }
          return data
        }

        handleError(
          error?.response?.status,
          `${API_DOCUMENT_COLLECTION_PATH}/${id}/documents`,
          error.response || error
        )
      }
    },
    {
      enabled: Boolean(id),
      useErrorBoundary: true,
      getNextPageParam({ meta }) {
        if (meta.total <= (meta?.offset ?? 0) + params.limit) return

        return {
          offset: meta.offset + params.limit,
          limit: params.limit,
          total: meta.total,
        }
      },
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000, // 1 hour
    }
  )
}

export type ResponseSuccessGetDocument = {
  data: TDocument
}

export function useAPIDocumentCollectionDocument(id: string | null) {
  const apiPath = `${API_DOCUMENT_PATH}/${id}`

  return useQuery<ResponseSuccessGetDocument>(
    apiPath,
    async () => {
      try {
        const response = (
          await axios.get<any, AxiosResponse<ResponseSuccessGetDocument>>(
            `${baseUrl}${apiPath}`
          )
        ).data
        return response
      } catch (error) {
        handleError(error?.response?.status, apiPath, error.response || error)
      }
    },
    {
      enabled: Boolean(id),
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000, // 1 hour
    }
  )
}

export function useAPIDocumentCollectionCategoryDetails(id: string | string[]) {
  const apiPath = `${API_DOCUMENT_COLLECTION_PATH}/${id}`

  return useQuery<ResponseSuccessGetDocumentCategoryDetail>(
    apiPath,
    async () => {
      try {
        const response = (
          await axios.get<
            any,
            AxiosResponse<ResponseSuccessGetDocumentCategoryDetail>
          >(`${baseUrl}${apiPath}`)
        ).data
        return response
      } catch (error) {
        handleError(error?.response?.status, apiPath, error.response || error)
      }
    },
    {
      enabled: Boolean(id),
      useErrorBoundary: true,
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000, // 1 hour
    }
  )
}
